import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PmsType, StayNTouchPmsSetup } from '../../../../../types/pms';

const ONBOARDING_EMAIL = 'onboarding@stayntouch.com';

@Component({
    selector: 'juyo-stay-n-touch-connect',
    styleUrl: './stay-n-touch.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule],
    template: `
        <h3 class="step-title">Connect to StayNTouch</h3>
        <p class="step-subtitle">
            Enter the StayNTouch API credentials issued for your property. Don't have them yet? See
            <strong>How to get your credentials</strong> below.
        </p>

        <div class="field">
            <label class="field-label" for="snt-base-url">CONNECT API URL <span class="required">*</span></label>
            <input
                id="snt-base-url"
                type="text"
                class="field-input"
                placeholder="https://api.stayntouch.com/connect"
                [ngModel]="baseUrl()"
                (ngModelChange)="baseUrl.set($event)"
            />
            <p class="field-hint">
                Full URL including <strong>https://</strong> — e.g. https://api.stayntouch.com/connect
            </p>
        </div>

        <div class="field-grid">
            <div class="field">
                <label class="field-label" for="snt-token-url">AUTH URL <span class="required">*</span></label>
                <input
                    id="snt-token-url"
                    type="text"
                    class="field-input"
                    placeholder="auth.stayntouch.com"
                    [ngModel]="tokenUrl()"
                    (ngModelChange)="tokenUrl.set($event)"
                />
                <p class="field-hint">Host only, no <strong>https://</strong> — e.g. auth.stayntouch.com</p>
            </div>

            <div class="field">
                <label class="field-label" for="snt-hotel-id">HOTEL ID <span class="required">*</span></label>
                <input
                    id="snt-hotel-id"
                    type="text"
                    inputmode="numeric"
                    class="field-input"
                    placeholder="e.g. 12345"
                    [ngModel]="hotelId()"
                    (ngModelChange)="hotelId.set($event)"
                />
                <p class="field-hint">Identifies your specific property — differs per hotel within a chain.</p>
            </div>

            <div class="field">
                <label class="field-label" for="snt-client-id">CLIENT ID <span class="required">*</span></label>
                <input
                    id="snt-client-id"
                    type="text"
                    class="field-input"
                    placeholder="Chain OAuth2 client ID"
                    [ngModel]="clientId()"
                    (ngModelChange)="clientId.set($event)"
                />
                <p class="field-hint">Chain-level credential from StayNTouch — shared across your group.</p>
            </div>

            <div class="field">
                <label class="field-label" for="snt-client-secret">CLIENT SECRET <span class="required">*</span></label>
                <div class="field-input-wrapper">
                    <input
                        id="snt-client-secret"
                        [type]="showSecret() ? 'text' : 'password'"
                        class="field-input field-input--secret"
                        placeholder="Paste the client secret"
                        [ngModel]="clientSecret()"
                        (ngModelChange)="clientSecret.set($event)"
                    />
                    <button
                        type="button"
                        class="secret-toggle"
                        [attr.aria-label]="showSecret() ? 'Hide client secret' : 'Show client secret'"
                        [attr.aria-pressed]="showSecret()"
                        (click)="showSecret.set(!showSecret())"
                    >
                        <span class="material-symbols-outlined">
                            {{ showSecret() ? 'visibility_off' : 'visibility' }}
                        </span>
                    </button>
                </div>
                <p class="field-hint">Shared securely with the onboarding team and stored encrypted.</p>
            </div>
        </div>

        <button
            type="button"
            class="credentials-toggle"
            (click)="showCredentialsHelp.set(!showCredentialsHelp())"
            [attr.aria-expanded]="showCredentialsHelp()"
            aria-controls="snt-credentials-panel"
        >
            <span class="material-symbols-outlined">
                {{ showCredentialsHelp() ? 'expand_more' : 'chevron_right' }}
            </span>
            Don't have these yet? How to get your credentials
        </button>

        @if (showCredentialsHelp()) {
            <div class="credentials-panel" id="snt-credentials-panel">
                <p class="credentials-intro">
                    StayNTouch issues the credentials once your hotel authorises access. Send the email below to
                    <strong>{{ onboardingEmail }}</strong> from an address on your hotel's own domain (anyone at the
                    hotel can send it). StayNTouch then provides your Connect API URL, Auth URL, Client ID, Client
                    Secret and Hotel ID to enter above.
                </p>

                <div class="email-card">
                    <div class="email-card-header">
                        <p class="email-recipient">
                            To: <strong>{{ onboardingEmail }}</strong>
                        </p>
                        <div class="email-actions">
                            <button type="button" class="email-action email-action--primary" (click)="onCopyEmail()">
                                <span class="material-symbols-outlined">
                                    {{ copied() ? 'check' : 'content_copy' }}
                                </span>
                                {{ copied() ? 'Copied' : 'Copy email' }}
                            </button>
                            <a class="email-action" [href]="mailtoLink()">Open in email app →</a>
                        </div>
                    </div>
                    <pre class="email-body">{{ emailBody() }}</pre>
                </div>
            </div>
        }

        <div class="next-box">
            <span class="material-symbols-outlined next-icon">info</span>
            <div class="next-content">
                <p class="next-title">What happens next</p>
                <p class="next-body">
                    Once you submit, Juyo validates the API connection, syncs your history, and configures your property
                    — usually within 3–5 days. We'll email you when you're live.
                </p>
            </div>
        </div>

        <p class="guide-link-text">
            Need more detail? Read the
            <a class="guide-link" [href]="guideUrl" target="_blank" rel="noopener noreferrer">
                full StayNTouch onboarding guide →
            </a>
        </p>
    `,
})
export class StayNTouchConnectComponent {
    public propertyName = input<string>('');
    public valueChange = output<StayNTouchPmsSetup | null>();

    protected readonly onboardingEmail = ONBOARDING_EMAIL;
    protected readonly guideUrl =
        'https://intercom-help.eu/juyo-analytics/en/articles/775382-stayntouch-onboarding-what-to-expect';

    protected baseUrl = signal('');
    protected tokenUrl = signal('');
    protected hotelId = signal('');
    protected clientId = signal('');
    protected clientSecret = signal('');
    protected showSecret = signal(false);
    protected showCredentialsHelp = signal(false);
    protected copied = signal(false);

    protected emailBody = computed(() => {
        const property = this.propertyName().trim() || 'our hotel';

        return [
            'Good morning Onboarding team,',
            '',
            `We authorise Juyo Analytics to access the production data for ${property}. Please send us the API credentials (Connect API URL, Auth URL, Client ID, Client Secret and Hotel ID) needed for the Juyo integration.`,
            '',
            'Thanks!',
        ].join('\n');
    });

    protected mailtoLink = computed(() => {
        const subject = encodeURIComponent('Juyo Analytics integration — API credentials request');
        const body = encodeURIComponent(this.emailBody());

        return `mailto:${ONBOARDING_EMAIL}?subject=${subject}&body=${body}`;
    });

    private setup = computed<StayNTouchPmsSetup | null>(() => {
        const baseUrl = this.baseUrl().trim();
        const tokenUrl = this.tokenUrl().trim();
        const hotelId = this.hotelId().trim();
        const clientId = this.clientId().trim();
        const clientSecret = this.clientSecret();

        if (!baseUrl || !tokenUrl || !clientId || !clientSecret || !/^\d+$/.test(hotelId)) {
            return null;
        }

        return {
            type: PmsType.StayNTouch,
            baseUrl,
            tokenUrl,
            hotelId: Number(hotelId),
            clientId,
            clientSecret,
        };
    });

    constructor() {
        effect(() => this.valueChange.emit(this.setup()));
    }

    protected onCopyEmail(): void {
        navigator.clipboard.writeText(this.emailBody()).then(() => {
            this.copied.set(true);
            setTimeout(() => this.copied.set(false), 2000);
        });
    }
}
